import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../../core/redux/api/reduxStandardApiAction';
import * as R from 'ramda';
import { apiAddToHotlist } from '../../core/redux/api/endpoints';
import { IAppState } from '../../core/redux/IAppState';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { addError, addLoading, removeLoadingAndError } from '../../core/redux/api/module';
import { hasTrippedBreaker, updateBreaker } from '../../core/redux/api/circuitBreaker/module';
import { QuickEntitlement, EntitlementHotlist } from '../entitlements/models';
import { setEntitlementHotlistTitle } from '../entitlements/module';
import { Resort } from '../../resortDetails/resort';
import { assoc } from '../../language/module';
import { formatISO } from 'date-fns';
import { toDateOnly } from '../../language/time';


const KEY_ACTION = ACTION_TYPE.ADD_TO_HOTLIST_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.ADD_TO_HOTLIST_REQUEST,
  ACTION_TYPE.ADD_TO_HOTLIST_RESPONSE,
  ACTION_TYPE.ADD_TO_HOTLIST_ERROR,
];


/**
 * Constructs an action that can be dispatched to add a guest to the hotlist (blacklist the guest's passes)
 * NOTE:  Date inputs for hotlist are formated to only include date (not time) so the date will be localized by default
 * Dates should be sent to API in ISO 8601 format
 */
export const addToHotlistAction = (
  masterGuestId: string,
  entitlement: QuickEntitlement,
  resortAbbreviation: string,
  isHotlistGlobal: boolean,
  effectiveDate: Date,
  expirationDate: Date,
  hotlistTypeId: number,
  commentText: string,
  resortList: Resort[]
  ) => makeApiAction(
  {
    method:   'POST',
    endpoint: (state) => apiAddToHotlist(state.selectedResort.resortAbbreviation),
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    body:     state => JSON.stringify({
      userId:          state.userState.userId,
      resortAbbreviation:        state.selectedResort.resortAbbreviation,
      commentText:     commentText,
      effectiveDate:   formatISO(toDateOnly(effectiveDate)!, { representation: 'complete'}),
      entitlementId:   entitlement.entitlementId,
      expirationDate:  formatISO(toDateOnly(expirationDate)!, { representation: 'complete'}),
      hotlistTypeId:   hotlistTypeId,
      isHotlistGlobal: isHotlistGlobal,
      masterGuestId:   masterGuestId,
      modifiedById:    state.userState.userName + ' - ' + state.selectedResort.resortAbbreviation,
      resort:          resortAbbreviation
    }),
    types:    ACTIONS.map(action => ({
       type: action,
       meta: {
         entitlement,
         resortList,
         hotlistData: {
           commentText,
           effectiveDate:   formatISO(toDateOnly(effectiveDate)!, { representation: 'complete'}),
           expirationDate:  formatISO(toDateOnly(expirationDate)!, { representation: 'complete'}),
           createdDate:     new Date(),
           entitlementId:   entitlement.entitlementId,
           hotlistTypeId,
           isHotlistGlobal,
           masterGuestId,
           resortAbbreviation,
           hotlistComment:  commentText,
         }
       },
    })) as RsaaTypes,
    bailout:  state => hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<undefined> = (state, action, error, isLoading, payload) => {
  const { masterGuestId } = action.meta.hotlistData;
  const entitlement: QuickEntitlement = action.meta.entitlement;
  const resortList: Resort[] = action.meta.resortList;
  const entitlementId = String(entitlement.entitlementId)
  const newHotlist =
    R.mergeDeepRight({
      entitlementHotlistId: Number(payload),
      status: 'Active',
      modifiedById: state.userState.userName + ' - ' + state.selectedResort.resortAbbreviation,
      createdById: state.userState.userName + ' - ' + state.selectedResort.resortAbbreviation,
    }, action.meta.hotlistData) as EntitlementHotlist;


  if (isLoading) {
    return addLoading(state, masterGuestId, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(withBreaker, masterGuestId, KEY_ACTION, error);
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, masterGuestId, KEY_ACTION);

  // Add a new active hotlist entry without hitting the remote API to refresh all hotlists
  const existingHotlist =
    R.append<EntitlementHotlist>(setEntitlementHotlistTitle(newHotlist, entitlement, resortList),
      noLoadingOrError.entitlementHotlists.getOrDefault(entitlementId, []));

  const alteredHotlists = noLoadingOrError.entitlementHotlists.add(entitlementId, existingHotlist);

  // Add a hotlist entry to the entitlement hotlist in the store
  return assoc('entitlementHotlists', alteredHotlists, noLoadingOrError);
};


export const addToHotlistReducer = makeApiReducer<undefined>(ACTIONS, KEY_ACTION, reducer);
